// Provider-agnostic entry point. Apps call initObservability() at startup,
// captureError() in catch blocks, withSpan() around hot operations, and
// shutdownObservability() on graceful exit. The package is a no-op until
// SENTRY_DSN or OTEL_EXPORTER_OTLP_ENDPOINT is set; callers who haven't
// opted in pay only the cost of a few null checks.

import { captureSentryError, initSentry, shutdownSentry } from './sentry.js';
import { initTracing, shutdownTracing, withOtelSpan } from './tracing.js';
import type { ObservabilityHandle, ObservabilityOptions, OtelSpan } from './types.js';

export type {
  ObservabilityHandle,
  ObservabilityOptions,
  OtelSpan,
  OtelTracer,
  SentrySdk,
} from './types.js';

let handle: ObservabilityHandle | null = null;

/**
 * Wire up whatever providers are configured. Safe to call once at process
 * start. Returns a handle whose `active` flag tells you whether anything
 * actually loaded — useful for logging "observability off" at startup
 * without crashing.
 */
export async function initObservability(opts: ObservabilityOptions): Promise<ObservabilityHandle> {
  if (handle) return handle;

  const sentry = await initSentry(opts);
  const tracing = await initTracing(opts);

  handle = {
    active: sentry !== null || tracing !== null,
    sentryEnabled: sentry !== null,
    tracingEnabled: tracing !== null,
    async shutdown() {
      await Promise.all([shutdownSentry(), shutdownTracing()]);
      handle = null;
    },
  };
  return handle;
}

/**
 * Report an error to whichever error backend is configured.
 * Always also logs to stderr so nothing is silently swallowed.
 */
export function captureError(err: unknown, ctx?: Record<string, unknown>): void {
  // Always log locally — don't let observability replace good old stderr.
  if (ctx && Object.keys(ctx).length > 0) {
    console.error(`[error] ${String(err)}`, ctx);
  } else {
    console.error(`[error] ${String(err)}`);
  }
  captureSentryError(err, ctx);
}

/**
 * Run fn inside a tracing span. When tracing is off, fn is called with
 * `null` and we skip the span bookkeeping entirely.
 */
export async function withSpan<T>(
  name: string,
  fn: (span: OtelSpan | null) => T | Promise<T>,
  attrs?: Record<string, unknown>,
): Promise<T> {
  return withOtelSpan(name, fn, attrs);
}

/** Flush + tear down providers. Call on SIGTERM. */
export async function shutdownObservability(): Promise<void> {
  if (!handle) return;
  await handle.shutdown();
}
